import React, { useEffect, useRef, useState } from 'react';
import { Trash2, X } from 'lucide-react';

function HorizonDeleteDialog({ horizon, onDelete, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pending = useRef(false);
  const dialog = useRef(null);
  const cancel = useRef(null);

  useEffect(() => {
    const trigger = document.activeElement;
    cancel.current?.focus();
    const keyboard = event => {
      if (event.key === 'Escape' && !pending.current) {
        event.preventDefault();
        onClose();
      }
      if (event.key !== 'Tab') return;
      const controls = Array.from(dialog.current?.querySelectorAll('button:not([disabled])') || []);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || !dialog.current.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.current.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', keyboard);
    return () => {
      document.removeEventListener('keydown', keyboard);
      if (trigger?.isConnected) trigger.focus();
    };
  }, [onClose]);

  const close = () => { if (!pending.current) onClose(); };
  const remove = async event => {
    event.preventDefault();
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      await onDelete(horizon);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Could not delete this Horizon.');
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={close}>
      <form ref={dialog} className="modal-content hzpage-create-dialog hzpage-delete-dialog"
        role="alertdialog" aria-modal="true" aria-labelledby="hzpage-delete-title"
        aria-describedby="hzpage-delete-description" aria-busy={busy}
        onSubmit={remove} onClick={event => event.stopPropagation()}>
        <div className="hzpage-create-header">
          <h2 id="hzpage-delete-title"><Trash2 size={20} /> Delete Horizon</h2>
          <button type="button" className="btn btn-ghost hzpage-create-close" aria-label="Close Delete Horizon"
            disabled={busy} onClick={close}><X size={20} /></button>
        </div>
        <div className="hzpage-create-body">
          <p id="hzpage-delete-description">
            Permanently delete <strong>{horizon.name}</strong> and all its scheduling entries, lanes, and milestones?
            {' '}Board tasks and their current due dates will be kept. This cannot be undone.
          </p>
          {error && <div className="hzpage-create-error" role="alert">{error}</div>}
        </div>
        <div className="hzpage-dialog-actions">
          <button ref={cancel} type="button" className="btn btn-secondary hzpage-delete-cancel" disabled={busy} onClick={close}>Cancel</button>
          <button type="submit" className="btn btn-danger hzpage-delete-confirm" disabled={busy}>
            {busy ? 'Deleting…' : 'Delete Horizon'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default HorizonDeleteDialog;
